export type ProjectType =
  | 'mobile-app'
  | 'web-app'
  | 'software'
  | 'ai-agent'
  | 'saas-mvp'
  | 'integration'
  | 'ecommerce'
  | 'consultation';

export type ScopeSize = 'small' | 'medium' | 'large';

export type BuildIntake = {
  projectType: ProjectType;
  projectName: string;
  company: string;
  contactName: string;
  email: string;
  description: string;
  platforms: string[];
  scopeSize: ScopeSize;
  needsDesign: boolean;
  needsAuth: boolean;
  needsPayments: boolean;
  needsAI: boolean;
  needsAdminDashboard: boolean;
  timeline: 'standard' | 'rush';
  /** Optional 1-on-1 sessions with Milan Cheeks @ $100/hr (Build add-on) */
  consultationHours: number;
  /** Strategy session packages on /consultation: 30 | 60 | 90 minutes */
  consultationMinutes?: number;
  conversationNotes?: string;
};

export type ConversationTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export type QuoteLineItem = {
  label: string;
  amountCents: number;
};

export type BuildQuoteResult = {
  projectType: ProjectType;
  projectTypeLabel: string;
  lineItems: QuoteLineItem[];
  baseTotalCents: number;
  currentTotalCents: number;
  discountPercent: number;
  negotiationRound: number;
  maxDiscountPercent: number;
  timelineWeeks: string;
  depositCents: number;
  summary: string;
  valueDefense: string[];
  llmGenerated?: boolean;
  quoteRationale?: string;
};

export type SavedBuildQuote = {
  id: string;
  createdAt: string;
  intake: BuildIntake;
  quote: BuildQuoteResult;
};

export const PROJECT_TYPE_OPTIONS: { id: ProjectType; label: string; hint: string }[] = [
  { id: 'mobile-app', label: 'Mobile app', hint: 'iOS, Android, or cross-platform' },
  { id: 'web-app', label: 'Web application', hint: 'Dashboards, portals, internal tools' },
  { id: 'software', label: 'Software', hint: 'Desktop apps, internal tools, custom platforms' },
  { id: 'ai-agent', label: 'AI agent / automation', hint: 'Agents, workflows, integrations' },
  { id: 'saas-mvp', label: 'SaaS / software platform', hint: 'Multi-tenant product MVP' },
  { id: 'integration', label: 'Integration / API work', hint: 'Connect systems, APIs, data pipes' },
  { id: 'ecommerce', label: 'E-commerce / marketplace', hint: 'Storefronts, checkout, catalog' },
  {
    id: 'consultation',
    label: 'Strategy session',
    hint: '30 / 60 / 90 min with Milan Cheeks',
  },
];

export const DEPOSIT_PERCENT = 30;
export const CONSULTATION_HOURLY_USD = 100;
export const CONSULTATION_FOUNDER = 'Milan Cheeks';
export const CONSULTATION_FOUNDER_TITLE = 'Founder & CEO, SELF Labs Inc';
export const CONSULTATION_HOUR_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 30, 40] as const;
export const CONSULTATION_ONLY_HOUR_OPTIONS = CONSULTATION_HOUR_OPTIONS.filter((h) => h > 0);

export type ConsultationStrategyMinutes = 30 | 60 | 90;

export const CONSULTATION_STRATEGY_PACKAGES: {
  minutes: ConsultationStrategyMinutes;
  priceCents: number;
  priceUsd: number;
  label: string;
  recommended: boolean;
}[] = [
  { minutes: 30, priceCents: 7_500, priceUsd: 75, label: '30 Minutes', recommended: false },
  { minutes: 60, priceCents: 20_000, priceUsd: 200, label: '60 Minutes', recommended: true },
  { minutes: 90, priceCents: 30_000, priceUsd: 300, label: '90 Minutes', recommended: false },
];

export const DEFAULT_CONSULTATION_STRATEGY_MINUTES: ConsultationStrategyMinutes = 60;

export function getConsultationStrategyPackage(minutes: unknown) {
  const value = Number(minutes);
  return CONSULTATION_STRATEGY_PACKAGES.find((pkg) => pkg.minutes === value) ?? null;
}

export function isConsultationProjectType(projectType: ProjectType): boolean {
  return projectType === 'consultation';
}
